import { Inject, Injectable, LOCALE_ID } from '@angular/core';
import { formatDate } from '@angular/common';

/** One display line for nested key/value OSINT field values. */
export type StructuredDisplayLine =
  | { kind: 'pair'; key: string; value: string }
  | { kind: 'plain'; value: string };

@Injectable({
  providedIn: 'root',
})
export class UtilsService {
  constructor(@Inject(LOCALE_ID) private locale: string) {}

  isLink(value: any): boolean {
    if (typeof value !== 'string') {
      return false;
    }

    const trimmedValue = value.trim().toLowerCase();
    return (
      trimmedValue.startsWith('http://') ||
      trimmedValue.startsWith('https://') ||
      trimmedValue.startsWith('data:image/')
    );
  }

  /** Tc Names cards show the name list without a field label. */
  isTcNamesPlatform(platformResult: any): boolean {
    const raw = String(
      platformResult?.platformKey ||
        platformResult?.platform ||
        platformResult?.name ||
        '',
    )
      .trim()
      .toLowerCase()
      .replace(/[\s-]+/g, '_');
    return raw === 'tc_names' || raw === 'names';
  }

  toLink(value: any): string {
    return typeof value === 'string' ? value.trim() : value;
  }

  isBase64Image(value: any): boolean {
    if (typeof value !== 'string') {
      return false;
    }

    const trimmedValue = value.trim().toLowerCase();
    return (
      trimmedValue.startsWith('data:image/') && trimmedValue.includes('base64,')
    );
  }

  isIsoDate(value: any): boolean {
    if (typeof value !== 'string') {
      return false;
    }

    const trimmedValue = value.trim();
    return /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?(?:Z|[+-]\d{2}:\d{2})?$/.test(
      trimmedValue,
    );
  }

  formatDateTime(value: any, format: string = 'medium'): string {
    if (!this.isIsoDate(value)) {
      return value;
    }

    try {
      return formatDate(value, format, this.locale);
    } catch {
      return value;
    }
  }

  /**
   * Format a plain object as human-readable key/value text.
   * Used for list items and object fields across Basic + OSINT search UIs.
   * Each key/value pair is on its own line.
   * Example: { upi_id: "a@ybl", name: "A" } → "name: A\nupi_id: a@ybl"
   */
  formatKeyValueObject(value: any): string {
    if (value === null || value === undefined) {
      return '';
    }
    if (typeof value !== 'object' || Array.isArray(value)) {
      return String(value);
    }

    // Prefer a stable order for common id/name pairs, then remaining keys.
    const preferredKeys = [
      'name',
      'full_name',
      'upi_id',
      'UPI_Id',
      'vpa',
      'username',
      'id',
      'uid',
      'subscriber_id',
      'email',
      'phone',
      'status',
      'balance',
      'balance_end_date',
      'balance_token',
      'value',
    ];
    const seen = new Set<string>();
    const parts: string[] = [];

    const pushEntry = (key: string, raw: unknown) => {
      if (seen.has(key)) return;
      if (raw === null || raw === undefined) return;
      if (typeof raw === 'string' && !raw.trim()) return;
      seen.add(key);

      let display: string;
      if (typeof raw === 'object') {
        display = Array.isArray(raw)
          ? raw.map((item) => this.formatListItemForDisplay(item)).filter(Boolean).join('\n')
          : this.formatKeyValueObject(raw);
      } else {
        display = String(raw).trim();
      }
      if (!display) return;
      parts.push(`${key}: ${display}`);
    };

    for (const key of preferredKeys) {
      if (Object.prototype.hasOwnProperty.call(value, key)) {
        pushEntry(key, value[key]);
      }
    }
    for (const [key, raw] of Object.entries(value)) {
      pushEntry(key, raw);
    }

    return parts.join('\n');
  }

  /** Format one list/array item for UI display (primitives stay as-is; objects → key/value). */
  formatListItemForDisplay(item: any, detailLabel: string = ''): string {
    if (item === null || item === undefined) {
      return '';
    }

    let normalized: string;
    if (typeof item === 'string') {
      // Legacy API strings: "upi_id: x | name: y" → newline-separated pairs
      normalized = this.expandPipeSeparatedKeyValues(item.trim());
    } else if (typeof item === 'number' || typeof item === 'boolean') {
      normalized = String(item);
    } else if (Array.isArray(item)) {
      normalized = item
        .map((nested) => this.formatListItemForDisplay(nested))
        .filter((part) => part.length > 0)
        .join('\n');
    } else if (typeof item === 'object') {
      // Contract-shaped nested field: { type, value }
      if ('value' in item && ('type' in item || 'osint-search-type' in item)) {
        return this.formatListItemForDisplay(item.value, detailLabel);
      }
      normalized = this.formatKeyValueObject(item);
    } else {
      normalized = String(item);
    }

    if (!normalized) {
      return '';
    }

    if (detailLabel) {
      const prefix = `${detailLabel}:`;
      if (normalized.toLowerCase().startsWith(prefix.toLowerCase())) {
        normalized = normalized.slice(prefix.length).trim();
      }
    }

    return normalized;
  }

  /**
   * Convert legacy "key: value | key: value" strings into newline-separated pairs.
   * Leaves normal strings (and lone `|` usages) unchanged.
   */
  expandPipeSeparatedKeyValues(text: string): string {
    if (!text || typeof text !== 'string') {
      return '';
    }
    const trimmed = text.trim();
    // Require at least one " | key:" pattern so we don't rewrite unrelated text
    if (!/\|\s*[A-Za-z_][\w]*\s*:/.test(trimmed)) {
      return trimmed;
    }
    return trimmed
      .split(/\s*\|\s*/)
      .map((part) => part.trim())
      .filter((part) => part.length > 0)
      .join('\n');
  }

  /** Map a list/array field value to display-ready strings (or leave primitives unchanged). */
  normalizeListDisplayValue(value: any): any {
    if (!Array.isArray(value)) {
      return value;
    }
    if (value.length === 0) {
      return value;
    }

    const needsFormatting = value.some(
      (item) =>
        (item !== null && typeof item === 'object') ||
        (typeof item === 'string' && /\|\s*[A-Za-z_][\w]*\s*:/.test(item)),
    );
    if (!needsFormatting) {
      return value;
    }

    return value
      .map((item) => this.formatListItemForDisplay(item))
      .filter((item: string) => item.length > 0);
  }

  /** Join already-formatted list display strings (always newline for Basic + OSINT). */
  joinDisplayListItems(items: string[]): string {
    return (items || []).filter((item) => String(item || '').trim().length > 0).join('\n');
  }

  /** Address fields that arrive as a list should render one bullet per address. */
  isAddressListField(detail: { label?: unknown } | null | undefined): boolean {
    return String(detail?.label || '').trim().toLowerCase() === 'address';
  }

  /**
   * Split a shown date-time on the comma before the clock time.
   * "18 Sep 2017, 06:42:46 PM IST" → ["18 Sep 2017", "06:42:46 PM IST"]
   * "Oct 11, 2026, 1:13:15 AM" → ["Oct 11, 2026", "1:13:15 AM"]
   * ISO timestamps are formatted first, then split the same way.
   */
  splitDateTimeDisplay(value: unknown): [string, string] | null {
    if (typeof value !== 'string') {
      return null;
    }
    let text = value.trim();
    if (!text) {
      return null;
    }
    if (this.isIsoDate(text)) {
      const formatted = this.formatDateTime(text);
      if (typeof formatted !== 'string' || !formatted.trim() || formatted === text) {
        return null;
      }
      text = formatted.trim();
    }

    const match = text.match(
      /^(.+),\s+(\d{1,2}:\d{2}(?::\d{2})?\s*[AP]M(?:\s+\S+)?)$/i,
    );
    if (!match) {
      return null;
    }
    const datePart = match[1].trim();
    const timePart = match[2].trim();
    if (!datePart || !timePart) {
      return null;
    }
    return [datePart, timePart];
  }

  /** True when a list value is a date line plus a time line from splitDateTimeDisplay. */
  isDateTimeBulletList(detail: { value?: unknown } | null | undefined): boolean {
    const value = detail?.value;
    if (!Array.isArray(value) || value.length !== 2) {
      return false;
    }
    if (value.some((item) => typeof item !== 'string')) {
      return false;
    }
    const parts = this.splitDateTimeDisplay(`${value[0]}, ${value[1]}`);
    return !!parts && parts[0] === value[0] && parts[1] === value[1];
  }

  /**
   * A list of separate plain values, such as
   * ["kaushal jii delhi", "Kaushal Pati", "Kaushal Gautam"].
   * Structured records and date/time splits keep their own layout.
   */
  isPlainStringList(detail: { label?: unknown; value?: unknown } | null | undefined): boolean {
    if (this.isAddressListField(detail) || this.isDateTimeBulletList(detail)) {
      return false;
    }
    const value = detail?.value;
    if (!Array.isArray(value) || value.length < 2) {
      return false;
    }
    return value.every((item) => {
      if (typeof item === 'number' || typeof item === 'boolean') {
        return true;
      }
      if (typeof item !== 'string') {
        return false;
      }
      const text = item.trim();
      return text.length > 0 && !this.hasStructuredDisplayPairs(text);
    });
  }

  /** Disc bullets: addresses, split date/times, and plain multi-value lists. */
  usesDiscBullets(detail: { label?: unknown; value?: unknown } | null | undefined): boolean {
    return (
      this.isAddressListField(detail) ||
      this.isDateTimeBulletList(detail) ||
      this.isPlainStringList(detail)
    );
  }

  formatDisplayValue(value: any): string {
    if (value === null || value === undefined) {
      return '';
    }

    if (this.isIsoDate(value)) {
      return this.formatDateTime(value);
    }

    if (typeof value === 'string') {
      return this.expandPipeSeparatedKeyValues(value.trim());
    }

    if (typeof value === 'number' || typeof value === 'boolean') {
      return String(value);
    }

    if (Array.isArray(value)) {
      if (value.length === 0) {
        return '[]';
      }
      return this.joinDisplayListItems(
        value.map((item) => this.formatListItemForDisplay(item)),
      );
    }

    if (typeof value === 'object') {
      return this.formatKeyValueObject(value);
    }

    return String(value);
  }

  /**
   * Split formatted display text into plain vs key/value lines so the UI can
   * style nested labels separately from green values.
   * Example: "name: A\nupi_id: a@ybl" → pair rows for each line.
   */
  parseStructuredDisplayText(
    text: string | null | undefined,
  ): StructuredDisplayLine[] {
    const raw = this.expandPipeSeparatedKeyValues(
      String(text ?? '')
        .replace(/\r\n/g, '\n')
        .trim(),
    );
    if (!raw) {
      return [];
    }

    return raw
      .split('\n')
      .map((line) => {
        const trimmed = line.trim();
        if (!trimmed) {
          return null;
        }

        const pair = this.matchStructuredKeyValue(trimmed);
        if (pair) {
          return { kind: 'pair' as const, key: pair.key, value: pair.value };
        }
        return { kind: 'plain' as const, value: trimmed };
      })
      .filter((line): line is StructuredDisplayLine => line !== null);
  }

  /** True when text has at least one key: value line worth structured styling. */
  hasStructuredDisplayPairs(text: string | null | undefined): boolean {
    return this.parseStructuredDisplayText(text).some(
      (line) => line.kind === 'pair',
    );
  }

  /** Pretty-print nested field keys: upi_id → UPI ID, subscriber_id → Subscriber ID. */
  formatStructuredDisplayKey(key: string): string {
    const raw = String(key || '').trim();
    if (!raw) {
      return '';
    }

    const known: Record<string, string> = {
      upi_id: 'UPI ID',
      upi: 'UPI',
      vpa: 'VPA',
      uid: 'UID',
      id: 'ID',
      name: 'Name',
      email: 'Email',
      phone: 'Phone',
      status: 'Status',
      amount: 'Amount',
      balance_token: 'Balance Token',
      subscriber_id: 'Subscriber ID',
      end_date: 'End Date',
      smsid: 'SMSID',
      vc: 'VC',
      city: 'City',
      state: 'State',
      pincode: 'Pincode',
      address: 'Address',
    };

    const lower = raw.toLowerCase().replace(/\s+/g, '_');
    if (known[lower]) {
      return known[lower];
    }

    // Already human-readable Title Case (e.g. "Subscriber ID")
    if (
      /^[A-Z][A-Za-z0-9]*(?:\s+[A-Z][A-Za-z0-9]*)*$/.test(raw) &&
      raw.includes(' ')
    ) {
      return raw;
    }

    return raw
      .replace(/[_-]+/g, ' ')
      .replace(/\s+/g, ' ')
      .trim()
      .replace(/\b\w/g, (ch) => ch.toUpperCase());
  }

  private matchStructuredKeyValue(
    line: string,
  ): { key: string; value: string } | null {
    // Require a space (or end) after the first colon so times/URLs stay intact.
    const match = line.match(/^([A-Za-z_][\w\s./-]{0,80}?):\s+(.+)$/);
    if (!match) {
      return null;
    }

    const key = match[1].trim();
    const value = match[2].trim();
    if (!key || !value) {
      return null;
    }

    const keyLower = key.toLowerCase();
    if (
      keyLower === 'http' ||
      keyLower === 'https' ||
      keyLower === 'ftp' ||
      keyLower === 'mailto' ||
      keyLower === 'data'
    ) {
      return null;
    }

    // Avoid treating very long "keys" (prose before a colon) as labels.
    if (key.length > 48) {
      return null;
    }

    return { key, value };
  }

  /**
   * Hide badge/style metadata objects like:
   * { "color": "#663399", "tolerance": 9, "flavor_text": "$count$ activites in $year$" }
   */
  isNonRenderableMetadataValue(value: any): boolean {
    if (value && typeof value === 'object' && !Array.isArray(value)) {
      return (
        Object.prototype.hasOwnProperty.call(value, 'color') &&
        Object.prototype.hasOwnProperty.call(value, 'tolerance') &&
        Object.prototype.hasOwnProperty.call(value, 'flavor_text')
      );
    }

    if (typeof value === 'string') {
      return /^\s*\{\s*"color"\s*:\s*"[^"]*"\s*,\s*"tolerance"\s*:\s*\d+\s*,\s*"flavor_text"\s*:\s*"[^"]*"\s*\}\s*$/.test(
        value.trim(),
      );
    }

    return false;
  }

  /** Long prose / list / URL fields should truncate with hover for full content. */
  isLongTextDetail(detail: any): boolean {
    if (detail?.type === 'list') {
      const values = Array.isArray(detail?.value) ? detail.value : [];
      return values.length > 0;
    }

    if (detail?.type === 'link') {
      return typeof detail?.value === 'string' && detail.value.trim().length > 0;
    }

    const label = String(detail?.label || '').toLowerCase();
    if (
      label.includes('description') ||
      label.includes('bio') ||
      label.includes('about') ||
      label.includes('summary') ||
      label.includes('note') ||
      label.includes('logo') ||
      label.includes('image') ||
      label.includes('url') ||
      label.includes('website') ||
      label.includes('link') ||
      label.includes('data class')
    ) {
      return true;
    }

    const value = detail?.value;
    if (typeof value === 'string') {
      const trimmed = value.trim();
      if (trimmed.length > 40 || this.isLink(trimmed)) {
        return true;
      }
    }

    return false;
  }

  /**
   * Creates a horizontally mirrored version of an image
   * Single Responsibility: Handles only image mirroring
   * @param imageUrl - The URL of the image to mirror
   * @returns Promise that resolves to a data URL of the mirrored image
   */
  createMirroredImage(imageUrl: string): Promise<string> {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';

      img.onload = () => {
        try {
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');

          if (!ctx) {
            reject(new Error('Could not get canvas context'));
            return;
          }

          canvas.width = img.width;
          canvas.height = img.height;

          ctx.translate(canvas.width, 0);
          ctx.scale(-1, 1);

          ctx.drawImage(img, 0, 0);

          const mirroredDataUrl = canvas.toDataURL('image/png');
          resolve(mirroredDataUrl);
        } catch (error) {
          reject(error);
        }
      };

      img.onerror = () => {
        reject(new Error('Failed to load image for mirroring'));
      };

      img.src = imageUrl;
    });
  }
}
